<template>
  <div class="chatbot-messages overflow-y-auto px-5 py-4 bg-gray-50 transition-all duration-300"
   ref="messagesContainer" @scroll="handleScroll">
    <!-- Empty state when no messages -->
    <div v-if="!messages || messages.length === 0" class="empty-state flex flex-col items-center justify-center h-full">
      <div class="bg-blue-100 rounded-full p-4 mb-4">
        <svg class="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
        </svg>
      </div>
      <h4 class="text-lg font-medium text-gray-900 mb-2">Welcome to Fraport Assistant!</h4>
      <p class="text-gray-600 text-center max-w-md">
        Hello {{ userData?.name || 'there' }}! I'm here to help you with your airport management tasks.
        Feel free to ask me anything.
      </p>
    </div>
    
    <!-- Message list -->
    <div v-else class="message-list space-y-6">
      <ChatbotMessage
        v-for="message in messages"
        :key="message.id"
        :message="message"
        :isUser="!message.is_ai_message"
        :userAvatar="userData?.avatar"
        :userName="userData?.name"
      />
    </div>
    
  </div>
      <!-- Scroll to bottom button -->
      <button
      v-if="showScrollButton"
      @click="scrollToBottom"
      class="scroll-to-bottom-button fixed bg-blue-300 text-white rounded-full p-3 shadow-lg hover:bg-blue-500 transition-all duration-300 z-10"
      style="position: absolute; bottom: 2rem; left: 50%;"
      aria-label="Scroll to bottom"
    >
      <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
      </svg>
    </button>

</template>

<script lang="ts">
import { defineComponent, PropType, ref, onUpdated, watch } from 'vue';
// Import ChatMessage type for props typing
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ChatMessage } from '@/types/chat';
import ChatbotMessage from './ChatbotMessage.vue';

interface UserData {
  name?: string;
  avatar?: string | null;
  business_role?: string;
  id?: string;
}

export default defineComponent({
  name: 'ChatbotMessages',
  components: {
    ChatbotMessage
  },
  props: {
    messages: {
      type: Array as PropType<ChatMessage[]>,
      default: () => []
    },
    userData: {
      type: Object as PropType<UserData>,
      default: () => ({
        name: 'User',
        avatar: null
      })
    }
  },
  setup(props) {
    const messagesContainer = ref<HTMLElement | null>(null);
    const showScrollButton = ref(false);
    const isNearBottom = ref(true);
    const previousMessagesLength = ref(0);
    const previousChatId = ref<string | null>(null);
    
    // Threshold in pixels to determine if we're near the bottom
    const SCROLL_THRESHOLD = 100;
    
    // Function to check if the scroll position is near the bottom
    const checkIfNearBottom = (): boolean => {
      if (!messagesContainer.value) return true;
      
      const { scrollTop, scrollHeight, clientHeight } = messagesContainer.value;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      
      return distanceFromBottom <= SCROLL_THRESHOLD;
    };
    
    // Function to scroll to the bottom
    const scrollToBottom = () => {
      if (messagesContainer.value) {
        messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
        showScrollButton.value = false;
        isNearBottom.value = true;
      }
    };
    
    // Handle scroll events to show/hide the scroll button
    const handleScroll = () => {
      isNearBottom.value = checkIfNearBottom();
      showScrollButton.value = !isNearBottom.value;
    };
    
    // Watch for changes in the messages array
    watch(() => props.messages, (newMessages) => {
      // Check if this is a new chat by looking at the first message ID
      const firstNewMessageId = newMessages.length > 0 ? newMessages[0].id : null;
      const currentChatId = firstNewMessageId ? firstNewMessageId.split('-')[0] : null;
      
      // If this is a new chat or the first load
      if (currentChatId && currentChatId !== previousChatId.value) {
        console.log('ChatbotMessages - New chat detected, scrolling to bottom');
        previousChatId.value = currentChatId;
        previousMessagesLength.value = newMessages.length;
        
        // Scroll to bottom with a slight delay to ensure DOM is updated
        setTimeout(scrollToBottom, 0);
        return;
      }
      
      // If new messages were added to the current chat
      if (newMessages.length > previousMessagesLength.value) {
        // Store the new length
        previousMessagesLength.value = newMessages.length;
        
        // If we're near the bottom or the last message is from the user, scroll to bottom
        const lastMessage = newMessages[newMessages.length - 1];
        if (isNearBottom.value || (lastMessage && !lastMessage.is_ai_message)) {
          // Use setTimeout to ensure the DOM has updated
          setTimeout(scrollToBottom, 0);
        }
      }
    }, { deep: true });
    
    // Initial setup when component is mounted
    onUpdated(() => {
      // Only scroll to bottom on first load
      if (previousMessagesLength.value === 0 && props.messages.length > 0) {
        console.log('ChatbotMessages - Initial load detected, scrolling to bottom');
        previousMessagesLength.value = props.messages.length;
        
        // Get the chat ID from the first message if available
        if (props.messages.length > 0) {
          const firstMessageId = props.messages[0].id;
          previousChatId.value = firstMessageId.split('-')[0];
        }
        
        scrollToBottom();
      }
    });
    
    return {
      messagesContainer,
      showScrollButton,
      scrollToBottom,
      handleScroll
    };
  }
});
</script>

<style scoped>
.chatbot-messages {
  background-color: #f9fafb;
  height: calc(100% - 10px); /* Ensure it doesn't overflow */
  overflow-y: auto;
  scroll-behavior: smooth;
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  flex: 1;
}

.chatbot-messages::-webkit-scrollbar {
  width: 6px;
}

.chatbot-messages::-webkit-scrollbar-track {
  background: transparent;
}

.chatbot-messages::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 145, 0.2);
  border-radius: 20px;
}

.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  background: linear-gradient(to bottom, rgba(249, 250, 251, 0) 0%, rgba(249, 250, 251, 1) 100%);
}

.message-list {
  padding: 1.5rem;
  padding-top: 2rem;
  flex: 1;
  min-height: 100%;
}

/* Add a subtle shadow at the top of the messages to create depth */
.chatbot-messages::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 10px;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.05), transparent);
  pointer-events: none;
}

/* Scroll to bottom button styles */
.scroll-to-bottom-button {
  animation: fadeIn 0.3s ease-in-out;
  box-shadow: 0 4px 12px rgba(0, 0, 145, 0.2);
}

.scroll-to-bottom-button:hover {
  transform: translateY(-2px);
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>